import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle,
  Check,
  CheckCheck,
  ChevronRight,
  ClipboardList,
  CreditCard,
  ExternalLink,
  FileText,
  Inbox as InboxIcon,
  Link2,
  MessageSquare,
  MessagesSquare,
  Paperclip,
  Pencil,
  Percent,
  RefreshCw,
  Reply,
  Search,
  Send,
  SmilePlus,
  ShoppingBag,
  StickyNote,
  Trash2,
  UserPlus,
  X,
} from 'lucide-react';
import {
  DEFAULT_DUE_TIME,
  TIME_SLOTS,
  computeTotals,
  formatMoney,
  humanAgentState,
  isClosingSoon,
  relativeTime,
  replyWindow,
  renderableEmoji,
  slotLabel,
  todayLocal,
  type ChannelType,
  type ConversationStatus,
  type Message,
  type MessageAttachment,
} from '@bakery/shared';

import { enterConfirms } from '../lib/keyboard.js';
import { api } from '../lib/api.js';
import { byName } from '../lib/catalog-order.js';
import { CustomerDetail } from './Customers.js';
import { ordersChanged } from '../lib/invalidate.js';
import { ItemNoteModal } from './ItemNote.js';
import { LineDiscountModal } from './LineDiscount.js';
import { Badge, Button, Card, cx, EmptyState, Field, Input, Modal, NumberInput, ROOMY_TARGET, Select, Skeleton, Tabs, Textarea, useToast } from '../components/ui.js';

/**
 * What somebody types after the slash to reach the menu.
 *
 * Matched on the start of the word, so `/a`, `/av` and `/menu` all find it
 * while `/pay` goes straight to the saved replies without the command getting
 * in the way. A bare slash matches every one of these, which is what makes the
 * list open the moment the key is pressed.
 */
const MENU_WORDS = ['available', 'menu', 'today', 'stock'];

/** A row in the list under the composer: a saved reply, or the one command. */
interface Suggestion {
  id: string;
  title: string;
  /** The single line shown under the title. */
  preview: string;
  /** What lands in the draft when it is chosen. */
  body: string;
  /** False while the menu is still being read off the sheet. */
  ready: boolean;
}

const CHANNEL_LABEL: Record<string, string> = {
  messenger: 'Messenger',
  instagram: 'Instagram',
  whatsapp: 'WhatsApp',
  viber: 'Viber',
  tiktok: 'TikTok',
  sms: 'SMS',
  email: 'Email',
  walk_in: 'Walk-in',
};

/** Meta's own ceiling is higher; this one fails fast on a phone connection. */
const MAX_ATTACHMENT_BYTES = 8 * 1024 * 1024;

/**
 * When each thread was last read back from Messenger.
 *
 * Opening a thread checks Meta for what the webhook missed, and clicking
 * between threads would otherwise call out every time. Kept in memory on
 * purpose: a page reload should check again.
 */
const lastPulled = new Map<string, number>();
const PULL_EVERY_MS = 60_000;

/**
 * A file as base64, without the data: prefix.
 *
 * Via FileReader rather than a loop over the bytes: building a binary string
 * with String.fromCharCode spread over a few million characters overruns the
 * call stack, and a shop photographing a cake will comfortably reach that.
 */
function base64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('That file could not be read'));
    reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
    reader.readAsDataURL(file);
  });
}

export function InboxScreen() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<ConversationStatus>('open');
  const [search, setSearch] = useState('');
  const [term, setTerm] = useState('');

  /*
   * Whether the order panel is open, held here rather than in the thread.
   *
   * The panel needs room, and the only room to give it is the column listing
   * the other conversations — which nobody is reading while they are writing
   * down what somebody has just asked for. It steps aside, and comes back.
   */
  const [orderOpen, setOrderOpen] = useState(false);

  // A different conversation is a different order. Never carry one across.
  useEffect(() => setOrderOpen(false), [conversationId]);

  // Typing shouldn't put a request on the wire per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => setTerm(search.trim()), 250);
    return () => clearTimeout(timer);
  }, [search]);

  /*
   * A search ignores the status tabs on purpose.
   *
   * Ninety of the shop's conversations are resolved, which is where anything
   * worth looking up has ended up. Searching inside the tab you happen to be
   * standing in would answer "no such customer" for almost every real query,
   * and the folder a thread was filed under is not something anybody
   * remembers. The tabs come back the moment the box is empty.
   */
  const searching = term.length >= 2;

  const list = useQuery({
    queryKey: ['conversations', searching ? `search:${term}` : status],
    queryFn: () => (searching ? api.inbox.conversations({ search: term, limit: 50 }) : api.inbox.conversations({ status })),
    // Polling would yank the list around underneath someone reading results.
    refetchInterval: searching ? false : 20_000,
  });

  const channels = useQuery({ queryKey: ['channels'], queryFn: api.inbox.channels });
  const connectedCount = channels.data?.filter((c) => c.channel?.status === 'connected').length ?? 0;

  return (
    <div className="flex h-full">
      {/* Thread list ------------------------------------------------------ */}
      <section
        className={cx(
          'flex w-full flex-col border-r border-[var(--color-line)] lg:w-[360px]',
          conversationId && 'hidden lg:flex',
          // Out of the way of the order panel, and back the moment it closes.
          orderOpen && 'lg:hidden',
        )}
      >
        <header className="space-y-3 border-b border-[var(--color-line)] px-4 py-3">
          <div className="flex items-center justify-between">
            <h1 className="font-display text-lg font-semibold">Inbox</h1>
            <Badge tone={connectedCount > 0 ? 'ok' : 'warn'}>
              {connectedCount} channel{connectedCount === 1 ? '' : 's'} live
            </Badge>
          </div>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[var(--color-ink-faint)]" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={(event) => event.key === 'Escape' && setSearch('')}
              placeholder="Search name, message, ticket…"
              className="h-10 w-full rounded-[var(--radius-md)] border border-[var(--color-line)] bg-transparent pl-9 pr-9 text-sm focus:border-crust-500 focus:outline-none"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-[var(--color-ink-faint)] hover:bg-[var(--color-surface-sunken)]"
              >
                <X className="size-4" />
              </button>
            )}
          </div>

          {searching ? (
            <p className="text-xs text-[var(--color-ink-soft)]">
              {list.isFetching
                ? 'Searching…'
                : `${list.data?.conversations.length ?? 0} match${
                    (list.data?.conversations.length ?? 0) === 1 ? '' : 'es'
                  } across every folder`}
            </p>
          ) : (
          <Tabs
            value={status}
            onChange={setStatus}
            tabs={[
              { value: 'open' as const, label: 'Open', count: list.data?.counts.open },
              { value: 'pending' as const, label: 'Pending', count: list.data?.counts.pending },
              { value: 'resolved' as const, label: 'Resolved' },
            ]}
          />
          )}
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {list.isLoading ? (
            <div className="space-y-2 p-3">
              {Array.from({ length: 5 }).map((_, index) => (
                <Skeleton key={index} className="h-16" />
              ))}
            </div>
          ) : list.data?.conversations.length === 0 ? (
            <EmptyState
              icon={searching ? <Search className="size-8" /> : <InboxIcon className="size-8" />}
              title={searching ? `Nothing matches “${term}”` : 'No conversations'}
              description={
                searching
                  ? 'Names, message text and ticket numbers are all searched, across every folder.'
                  : connectedCount === 0
                    ? 'Connect Messenger, WhatsApp or Viber in Settings and messages will land here.'
                    : 'Nothing waiting in this folder.'
              }
            />
          ) : (
            <ul className="divide-y divide-[var(--color-line)]">
              {list.data?.conversations.map((conversation) => (
                <li key={conversation.id}>
                  <button
                    onClick={() => navigate(`/inbox/${conversation.id}`)}
                    className={cx(
                      'w-full px-4 py-3 text-left transition hover:bg-[var(--color-surface-sunken)]',
                      conversationId === conversation.id && 'bg-[var(--color-accent-soft)]',
                    )}
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="truncate text-sm font-medium">{conversation.contactName}</span>
                      <span className="shrink-0 text-[11px] text-[var(--color-ink-faint)]">
                        {relativeTime(conversation.lastMessageAt)}
                      </span>
                    </div>
                    <p className="mt-0.5 line-clamp-2 text-xs text-[var(--color-ink-soft)]">
                      {conversation.lastMessagePreview}
                    </p>
                    <div className="mt-1.5 flex items-center gap-1.5">
                      <Badge tone="neutral">{CHANNEL_LABEL[conversation.channel] ?? conversation.channel}</Badge>
                      <span className="tabular text-[11px] text-[var(--color-ink-faint)]">{conversation.ticketNo}</span>
                      {conversation.unreadCount > 0 && <Badge tone="brand">{conversation.unreadCount} new</Badge>}
                      {conversation.orderId && <Badge tone="info">Order</Badge>}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* Thread ----------------------------------------------------------- */}
      <section className={cx('flex min-w-0 flex-1', !conversationId && 'hidden lg:flex')}>
        {conversationId ? (
          <>
            <div className="flex min-w-0 flex-1 flex-col">
              <Thread
                conversationId={conversationId}
                onBack={() => navigate('/inbox')}
                onOrderOpen={() => setOrderOpen(true)}
              />
            </div>
            {/*
              * Beside the conversation, never over it.
              *
              * Writing down a long order means reading it at the same time, and
              * a dialog that dims the thread behind it makes that impossible.
              * Staff were closing the form to check what had been asked for and
              * losing the lines they had already put in.
              */}
            {orderOpen && <OrderPanel conversationId={conversationId} onClose={() => setOrderOpen(false)} />}
          </>
        ) : (
          <div className="min-w-0 flex-1">
            <EmptyState
              icon={<MessagesSquare className="size-10" />}
              title="Pick a conversation"
              description="Replies, customer history and creating an order all happen here."
            />
          </div>
        )}
      </section>
    </div>
  );
}

/**
 * One attachment in a thread.
 *
 * Images show themselves; everything else is a link. The URLs Meta sends are
 * signed and expire after a while, so an old photo eventually stops loading —
 * the fallback says so rather than leaving a broken frame, because "this
 * expired" and "this never arrived" are very different problems for staff.
 */
/**
 * Close enough to the newest message to count as reading it.
 *
 * The allowance is about one message tall. Without it, a thread scrolled up by
 * a few pixels — a trackpad nudge, a rendering settle — would read as "looking
 * at history" and stop following the conversation.
 */
function nearBottom(element: HTMLElement | null): boolean {
  if (!element) return true;
  return element.scrollHeight - element.scrollTop - element.clientHeight < 160;
}

/**
 * What became of one outgoing message.
 *
 * Deliberately says only what is known. Messenger reports delivery and reading
 * as watermarks; most other channels report neither, and a shop that saw a
 * confident tick on an SMS would trust it. So an unreported message shows the
 * plain "sent" it has always shown, and nothing more.
 *
 * The exact times are on the hover, because "seen 3 minutes ago" answers the
 * question at a glance and "seen at 09:31" answers it in an argument.
 */
function Receipt({ message }: { message: Message }) {
  if (message.deliveryStatus === 'failed') {
    return <span className="font-medium">· not delivered</span>;
  }

  if (message.readAt) {
    return (
      <span className="inline-flex items-center gap-0.5" title={`Seen ${new Date(message.readAt).toLocaleString()}`}>
        · <CheckCheck className="size-3.5" /> Seen
      </span>
    );
  }

  if (message.deliveredAt) {
    return (
      <span
        className="inline-flex items-center gap-0.5"
        title={`Delivered ${new Date(message.deliveredAt).toLocaleString()}`}
      >
        · <CheckCheck className="size-3.5" /> Delivered
      </span>
    );
  }

  // Left here, nothing heard back. True of every channel that reports nothing.
  return (
    <span className="inline-flex items-center gap-0.5" title="Sent. This channel does not report delivery.">
      · <Check className="size-3.5" />
    </span>
  );
}

function Attachment({
  attachment,
  outbound,
  channel,
}: {
  attachment: MessageAttachment;
  outbound?: boolean;
  channel?: string;
}) {
  const [broken, setBroken] = useState(false);
  const label = attachment.name ?? attachment.type;

  if (!attachment.url) {
    /*
     * Three reasons for a missing URL, and they read very differently to
     * whoever is looking at the thread.
     *
     * One we sent ourselves: the bytes went straight to the platform and were
     * deliberately not kept, so the record is the filename. A card Messenger
     * drew — a payment request, a transfer — which this app is told nothing
     * about beyond its existence. Or WhatsApp handing over a media id rather
     * than a link, leaving the file inside WhatsApp.
     */
    const where = CHANNEL_LABEL[channel ?? ''] ?? 'the app';

    /*
     * A card is not a file. Drawn with a paperclip it looked like a document
     * somebody could open, and there is nothing behind it to open — so it gets
     * its own shape, and says plainly where the real thing lives.
     */
    /*
     * Cards recorded before they had a type of their own are recognised by
     * their shape: no file, and a name this app wrote. Re-reading them is
     * cheaper than rewriting months of stored messages, and an old payment is
     * exactly the thing somebody scrolls back to find.
     */
    const isCard =
      attachment.type === 'card' ||
      (attachment.type === 'file' && /(^Messenger .*card$|^Card$)/.test(attachment.name ?? ''));

    if (isCard) {
      return (
        <p className="flex items-start gap-1.5 rounded-[var(--radius-sm)] border border-current/20 px-2 py-1 text-xs opacity-90">
          <CreditCard className="mt-0.5 size-3.5 shrink-0" />
          <span className="min-w-0">
            {label}
            <span className="block opacity-70">Open it in {where}</span>
          </span>
        </p>
      );
    }

    return (
      <p className="flex items-center gap-1.5 text-xs opacity-80">
        <Paperclip className="size-3.5 shrink-0" />
        {outbound ? label : `${label} — open it in ${where}`}
      </p>
    );
  }

  /*
   * Anything served from Meta's own media hosts is a picture or a clip,
   * whatever type it was filed under on the way in.
   *
   * Story replies arrived for months under a kind this app did not recognise
   * and were stored as plain files, so the photograph a customer sent showed
   * as a paperclip. Deciding it here rather than rewriting those messages
   * fixes the ones already in the thread as well as the ones still to come,
   * and a URL that has since expired simply falls back to a link as before.
   */
  const platformMedia = /^https?:\/\/[^/]*(fbsbx\.com|fbcdn\.net|cdninstagram\.com)/i.test(attachment.url);
  const showAsPicture =
    attachment.type === 'image' || attachment.type === 'sticker' || (attachment.type === 'file' && platformMedia);

  if (showAsPicture && !broken) {
    return (
      <a href={attachment.url} target="_blank" rel="noreferrer" className="block">
        <img
          src={attachment.url}
          alt={label}
          loading="lazy"
          onError={() => setBroken(true)}
          className={cx(
            'max-h-64 w-auto rounded-[var(--radius-md)] object-cover',
            attachment.type === 'sticker' && 'max-h-28',
          )}
        />
      </a>
    );
  }

  return (
    <a
      href={attachment.url}
      target="_blank"
      rel="noreferrer"
      className="flex items-center gap-1.5 text-xs underline underline-offset-2 opacity-90 hover:opacity-100"
    >
      <Paperclip className="size-3.5 shrink-0" />
      {broken ? `${label} — the link has expired` : label}
    </a>
  );
}

function Thread({
  conversationId,
  onBack,
  onOrderOpen,
}: {
  conversationId: string;
  onBack: () => void;
  /**
   * Opening the order panel is the screen’s business rather than the thread’s:
   * the list of other conversations has to step aside to make room for it.
   */
  onOrderOpen: () => void;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [draft, setDraft] = useState('');
  /* Held while today's menu is being fetched, so a second press is ignored. */
  const pasting = useRef(false);
  const [pastingMenu, setPastingMenu] = useState(false);
  const [isNote, setIsNote] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  /*
   * Which suggestion the arrow keys have landed on, or -1 for none.
   *
   * It starts at -1 on purpose and only moves once somebody presses Down. The
   * list appearing must never change what Enter does — otherwise typing "pay"
   * and pressing Enter to send sends a template instead of the sentence you
   * were writing.
   */
  const [suggestion, setSuggestion] = useState(-1);
  const [suggestionsOff, setSuggestionsOff] = useState(false);
  /** The message this reply answers, shown above the box until it is sent. */
  const [replyingTo, setReplyingTo] = useState<Message | null>(null);
  /** Which bubble has its emoji row open, if any. */
  const [reactingTo, setReactingTo] = useState<string | null>(null);

  /*
   * The client's card, opened over the conversation rather than instead of it.
   *
   * Checking an address or correcting a phone number used to mean leaving for
   * the Clients screen, and coming back meant finding the thread again and
   * re-reading enough of it to remember what was being answered — with a
   * half-typed reply lost on the way. The card is the same one that screen
   * shows; closing it puts the chat back exactly as it was, because it never
   * went anywhere.
   */
  const [openCustomer, setOpenCustomer] = useState<string | null>(null);

  const react = useMutation({
    mutationFn: ({ id, emoji }: { id: string; emoji: string | null }) => api.inbox.react(id, emoji),
    onSuccess: () => {
      setReactingTo(null);
      queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
    },
    /*
     * The platform's own words. A reaction outside the messaging window, or on
     * a message too old for Meta to find, fails for a reason worth reading —
     * and unlike a reply there is no handoff link to offer instead.
     */
    onError: (error: Error) => toast.push({ tone: 'warn', title: 'Reaction not sent', body: error.message }),
  });

  /*
   * Templates, held here as well as in the picker so they can be offered while
   * somebody types. The shop has ninety-odd, which is past the point where
   * opening a list and reading it is faster than writing the sentence out.
   */
  const templates = useQuery({ queryKey: ['canned-replies'], queryFn: api.inbox.cannedReplies });

  const needle = draft.trim().toLowerCase();
  const explicit = needle.startsWith('/');
  const term = explicit ? needle.slice(1) : needle;

  /*
   * "What have you got today?" — the question the shop answers more than any
   * other, and the one template that cannot be saved as text because the answer
   * changes every morning. It is built from the bake sheet instead, so the
   * loaves quoted are the ones genuinely spare after the day's orders.
   *
   * Only fetched once somebody actually reaches for it. A conversation opened
   * to read should not go asking the kitchen what is on the shelf.
   *
   * Never cached, though. The sheet is filled in on another screen and usually
   * on another device — somebody at the counter types the morning's bake while
   * somebody else answers messages — so a held answer is one nobody here can
   * know has gone stale. Held for a minute, it quoted bread that had just been
   * added and left the shop reloading the page to see it. The request is small
   * and only goes when the command is reached for, which is exactly the moment
   * the answer has to be right.
   */
  const menuWanted = explicit && !suggestionsOff && !draft.includes('\n') && MENU_WORDS.some((word) => word.startsWith(term));
  const availableNow = useQuery({
    queryKey: ['available-message'],
    queryFn: () => api.bakeSheet.availableMessage(),
    enabled: menuWanted,
    staleTime: 0,
    gcTime: 0,
  });

  const bottom = useRef<HTMLDivElement>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  /** The thread already dropped to the bottom of, and how many messages it had. */
  const landed = useRef<{ id: string; count: number } | null>(null);

  const conversation = useQuery({
    queryKey: ['conversation', conversationId],
    queryFn: () => api.inbox.conversation(conversationId),
    refetchInterval: 15_000,
  });

  /*
   * Opening a thread lands on the newest message.
   *
   * This used to scroll smoothly from wherever the previous thread had left
   * the pane, after the paint — so the shop watched the top of a conversation
   * slide past, and on a long one gave up and scrolled by hand. It also keyed
   * only on the message count, so moving between two threads of the same
   * length never scrolled at all.
   */
  useLayoutEffect(() => {
    const messages = conversation.data?.messages;
    if (!messages) return;

    if (landed.current?.id !== conversationId) {
      landed.current = { id: conversationId, count: messages.length };
      // Before the first paint and without animating: the thread should simply
      // already be at the bottom, not arrive there.
      bottom.current?.scrollIntoView({ block: 'end' });
      return;
    }

    /*
     * Something arrived while this thread was open. Slide it into view only
     * for somebody already reading the newest messages — yanking the pane
     * while they are looking back through older ones is worse than leaving
     * them to scroll.
     */
    if (messages.length > landed.current.count) {
      landed.current = { id: conversationId, count: messages.length };
      if (nearBottom(scroller.current)) bottom.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [conversationId, conversation.data]);

  /*
   * An attachment that loads after the thread is painted pushes everything
   * down and leaves the newest message off the bottom again. A photo or a
   * payment card is exactly what the shop's threads end with, so without this
   * the jump above is undone a moment later by the picture arriving.
   */
  useEffect(() => {
    const element = scroller.current;
    if (!element) return;

    const observer = new ResizeObserver(() => {
      if (nearBottom(element)) bottom.current?.scrollIntoView({ block: 'end' });
    });
    for (const child of Array.from(element.children)) observer.observe(child);
    return () => observer.disconnect();
  }, [conversationId, conversation.data?.messages.length]);

  const reply = useMutation({
    mutationFn: () =>
      api.inbox.reply(conversationId, {
        body: draft,
        isPrivateNote: isNote,
        // A note stays in the building, so there is nothing on the platform
        // for it to quote.
        replyToMessageId: !isNote && replyingTo ? replyingTo.id : undefined,
      }),
    onSuccess: (result) => {
      setDraft('');
      setReplyingTo(null);
      queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });

      if ('delivered' in result && !result.delivered) {
        // Not connected, or the platform has no API. The text is saved either
        // way, and the operator gets a one-tap link to finish in the real app.
        toast.push({
          tone: 'warn',
          title: 'Saved, but not delivered',
          body: result.error ?? undefined,
          action: result.handoffUrl ? { label: 'Open the app to send', href: result.handoffUrl } : undefined,
        });
      }
    },
  });

  /**
   * Read the thread back from Messenger.
   *
   * Runs once when a thread is opened, quietly. Messenger writes messages of
   * its own into a thread — a payment transfer request is one — and those never
   * reach the webhook, so without this a customer can be asked to pay, and pay,
   * with no trace of it here.
   */
  const pullMissing = useMutation({
    mutationFn: () => {
      lastPulled.set(conversationId, Date.now());
      return api.inbox.syncConversation(conversationId);
    },
    onSuccess: (result) => {
      if (result.added > 0) {
        queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
        queryClient.invalidateQueries({ queryKey: ['conversations'] });
      }
    },
    // A thread that cannot be read back is not an error worth interrupting
    // anyone over: the messages that did arrive are all still here.
    onError: () => {},
  });

  const canPull = conversation.data?.channel === 'messenger' || conversation.data?.channel === 'instagram';

  useEffect(() => {
    const since = Date.now() - (lastPulled.get(conversationId) ?? 0);
    if (canPull && since > PULL_EVERY_MS) pullMissing.mutate();
    // Deliberately not depending on the mutation: it is recreated every render,
    // and listing it here would call Meta in a loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, canPull]);

  const sendFile = useMutation({
    mutationFn: async (file: File) => {
      if (file.size > MAX_ATTACHMENT_BYTES) {
        throw new Error(`${file.name} is ${Math.round(file.size / 1024 / 1024)} MB. The limit is 8 MB.`);
      }
      return api.inbox.sendAttachment(conversationId, {
        name: file.name,
        mimeType: file.type || 'application/octet-stream',
        data: await base64(file),
      });
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      if (!result.delivered) {
        // Recorded in the thread either way, so nobody sends it twice blind.
        toast.push({
          tone: 'warn',
          title: 'Saved, but not delivered',
          body: result.error ?? undefined,
          action: result.handoffUrl ? { label: 'Open the app to send', href: result.handoffUrl } : undefined,
        });
      }
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not send that file', body: error.message }),
  });

  const linkCustomer = useMutation({
    mutationFn: () => api.inbox.linkCustomer(conversationId),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
      toast.push({ tone: 'ok', title: `${result.customer.displayName} added to clients` });
    },
  });

  const setStatus = useMutation({
    mutationFn: (status: ConversationStatus) => api.inbox.update(conversationId, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
    },
  });

  if (conversation.isLoading) {
    return <div className="p-4"><Skeleton className="h-full min-h-64" /></div>;
  }

  /*
   * A thread that is not there any more.
   *
   * This used to sit on the loading skeleton for ever, which reads as a slow
   * network rather than a dead link — and it is now reachable, because an
   * order links back to the conversation it came from and that id can outlive
   * the thread, or arrive from a device whose messages have not synced yet.
   */
  if (!conversation.data) {
    return (
      <div className="flex h-full items-center justify-center p-4">
        <EmptyState
          icon={<MessagesSquare className="size-8" />}
          title="This conversation is not here"
          description="It may have been removed, or it may belong to a device that has not synced yet."
          action={<Button onClick={onBack}>Back to the inbox</Button>}
        />
      </div>
    );
  }

  const data = conversation.data;

  /*
   * Named `window` deliberately shadows nothing here — the thread never
   * touches the DOM global — and reads as what it is at the call sites below.
   */
  const window = replyWindow(data.channel, data.replyWindowExpiresAt);
  const humanAgent = humanAgentState(data.channel, data.replyWindowExpiresAt, data.lastInboundAt);
  const channelName = CHANNEL_LABEL[data.channel] ?? data.channel;

  /*
   * Templates offered as you type, the way Messenger does it.
   *
   * The needle is the whole draft, and it is matched *into* the title rather
   * than the other way round. That is what keeps the list from becoming noise:
   * "pay" matches "payment" and "Payment reminder", but carry on to "payment
   * received by GCash" and no title contains that, so the list closes by
   * itself. No dismissing, no mode to be in.
   *
   * A leading slash is the explicit way in — the shortcut this type has
   * carried a comment about since it was written — and it also searches the
   * body, for when you remember the wording but not the name.
   */
  const templateMatches =
    suggestionsOff || draft.includes('\n') || term.length < 2 || (!explicit && term.length > 30)
      ? []
      : (templates.data ?? [])
          .filter((t) =>
            explicit
              ? t.title.toLowerCase().includes(term) || t.body.toLowerCase().includes(term)
              : t.title.toLowerCase().includes(term),
          )
          .slice(0, 6);

  /*
   * One list, the command first. It sits above the saved replies because it is
   * the thing being reached for when somebody types a slash, and because it is
   * the only row whose text will be different tomorrow.
   */
  const suggestions: Suggestion[] = [
    ...(menuWanted
      ? [
          {
            id: 'menu:available',
            title: 'Available now',
            preview: availableNow.isPending
              ? 'Reading the bake sheet…'
              : availableNow.data?.text.replace(/\n+/g, ' · ') || 'Nothing on the sheet for today',
            body: availableNow.data?.text ?? '',
            ready: Boolean(availableNow.data?.text),
          },
        ]
      : []),
    ...templateMatches.map((t) => ({ id: t.id, title: t.title, preview: t.body, body: t.body, ready: true })),
  ];

  const useSuggestion = (chosen: Suggestion) => {
    // A row still loading is not an instruction to paste nothing.
    if (!chosen.ready) return;
    setDraft(chosen.body);
    setSuggestion(-1);
    setSuggestionsOff(true);
  };

  /**
   * Drop today's menu into the draft from the button rather than the slash.
   *
   * Asks the server every time, for the same reason the suggestion does: what
   * is on the shelf is decided on another screen, and a cached answer here is
   * one this screen has no way of knowing is out of date.
   */
  const pasteAvailableNow = async () => {
    /*
     * Pressed twice, the menu went in twice and the customer was sent the
     * whole price list two times over. It happens because this waits on the
     * server: the second press lands while the first is still in the air, so
     * neither sees the other's work.
     *
     * Two guards, because they catch different mistakes. The flag stops a
     * second press while the first is still fetching. The check below stops
     * one that arrives after it finished — a stray tap a moment later, which
     * no amount of disabling during the fetch would catch.
     */
    /*
     * The latch is a ref, not the state below it. Two taps a few milliseconds
     * apart both run before React has re-rendered, so each one still sees the
     * old value of a state flag and sails straight past it — which is exactly
     * how the message got sent twice in the first place. A ref changes the
     * moment it is written, so the second press reads what the first wrote.
     * The state is only there to grey the button out.
     */
    if (pasting.current) return;
    pasting.current = true;
    setPastingMenu(true);
    try {
      const menu = await queryClient.fetchQuery({
        queryKey: ['available-message'],
        queryFn: () => api.bakeSheet.availableMessage(),
        staleTime: 0,
      });
      if (!menu.text) {
        toast.push({ tone: 'warn', title: 'Nothing on the sheet', body: 'No goods are marked available for today yet.' });
        return;
      }
      if (draft.includes(menu.text.trim())) {
        // Said out loud, so a press that deliberately does nothing does not
        // read as the button being broken.
        toast.push({ tone: 'info', title: 'Already in this message' });
        return;
      }
      setDraft((current) => (current.trim() ? `${current.trimEnd()}\n\n${menu.text}` : menu.text));
      setSuggestionsOff(true);
    } finally {
      pasting.current = false;
      setPastingMenu(false);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <header className="flex flex-wrap items-center gap-3 border-b border-[var(--color-line)] px-4 py-3">
        <Button size="sm" variant="ghost" className="lg:hidden" onClick={onBack}>
          Back
        </Button>
        <div className="min-w-0 flex-1">
          {/*
            * The name at the top opens the card too.
            *
            * For nine conversations in ten the person's name is written here
            * twice — once in bold and again as the linked client below — and
            * only the second one did anything. So the obvious thing to click
            * was the one that was not a button, and the shop was clicking two
            * or three times before landing on the small blue one. Both work
            * now; whichever is reached for first is the right one.
            */}
          {data.customerId ? (
            <button
              type="button"
              onClick={() => setOpenCustomer(data.customerId)}
              className="block max-w-full truncate rounded-[var(--radius-sm)] text-left text-sm font-semibold underline decoration-dotted decoration-transparent underline-offset-2 transition hover:decoration-[var(--color-ink-soft)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-info)]"
            >
              {data.contactName}
            </button>
          ) : (
            <p className="truncate text-sm font-semibold">{data.contactName}</p>
          )}
          <p className="text-xs text-[var(--color-ink-soft)]">
            {CHANNEL_LABEL[data.channel] ?? data.channel} · {data.ticketNo}
            {/*
              * Once they are a client, their name is the way to their card.
              * Staff were reading it off this line and then going to find the
              * same person by hand under Clients — the detour the linked-order
              * banner below already exists to remove.
              */}
            {data.customerDisplayName && (
              <>
                {' · '}
                {data.customerId ? (
                  <button
                    type="button"
                    onClick={() => setOpenCustomer(data.customerId)}
                    className={cx(
                      ROOMY_TARGET,
                      'rounded-[var(--radius-sm)] font-medium text-[var(--color-info)] underline decoration-dotted underline-offset-2 transition hover:decoration-solid focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-info)]',
                    )}
                  >
                    {data.customerDisplayName}
                  </button>
                ) : (
                  data.customerDisplayName
                )}
              </>
            )}
          </p>
        </div>

        {/*
          * Their own row on a phone.
          *
          * Sharing one with the name left both squeezed into a few characters
          * each — the customer's name broken across three lines with "Create
          * order" sitting on top of it. There is room for all of it; there is
          * just not room for all of it side by side.
          */}
        <div className="flex w-full flex-wrap justify-end gap-1.5 sm:w-auto">
          {!data.customerId && (
            <Button size="sm" icon={<UserPlus className="size-4" />} loading={linkCustomer.isPending} onClick={() => linkCustomer.mutate()}>
              Add as client
            </Button>
          )}
          <Button size="sm" variant="primary" icon={<ShoppingBag className="size-4" />} onClick={onOrderOpen}>
            Create order
          </Button>
          <Select
            value={data.status}
            onChange={(event) => setStatus.mutate(event.target.value as ConversationStatus)}
            className="h-9 w-32"
          >
            <option value="open">Open</option>
            <option value="pending">Pending</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </Select>
        </div>
      </header>

      {data.linkedOrder && (
        /*
         * The banner is the way to the order, so it is a button rather than a
         * label. Staff were reading the order number off it and then going to
         * find it by hand on the board.
         */
        <button
          type="button"
          onClick={() => navigate(`/orders/${data.linkedOrder!.id}`)}
          className="flex w-full items-center gap-2 border-b border-[var(--color-line)] bg-[var(--color-info-soft)] px-4 py-2 text-left text-sm transition hover:brightness-95"
        >
          <Link2 className="size-4 shrink-0 text-[var(--color-info)]" />
          <span className="min-w-0 flex-1 truncate">
            Linked to <strong>{data.linkedOrder.orderNo}</strong> — {formatMoney(data.linkedOrder.total)},{' '}
            {data.linkedOrder.status.name}
          </span>
          {/*
            * Whether the money arrived, from the shop's own books rather than
            * from a card Messenger drew. Payment is recorded on all but a
            * handful of orders, which makes this the one answer in the thread
            * that can be trusted without opening Messenger to check.
            */}
          <Badge tone={data.linkedOrder.balanceDue === 0 ? 'ok' : 'warn'}>
            {data.linkedOrder.balanceDue === 0
              ? 'Paid'
              : `${formatMoney(data.linkedOrder.balanceDue)} unpaid`}
          </Badge>
          <span className="inline-flex shrink-0 items-center gap-1 text-xs text-[var(--color-info)]">
            Open order <ChevronRight className="size-3.5" />
          </span>
        </button>
      )}

      {/* Messages -------------------------------------------------------- */}
      <div ref={scroller} className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-4">
        {data.messages.map((message) => (
          <div
            key={message.id}
            className={cx(
              /*
               * `relative` so the emoji row can hang off the thread's own edge.
               * Anchoring it to the tools instead put it wherever the length of
               * the message happened to leave them, which on a short bubble was
               * half off the side of the screen.
               */
              'group relative flex items-center gap-1.5',
              message.direction === 'out' && !message.isPrivateNote ? 'justify-end' : 'justify-start',
            )}
          >
            {/*
              * Tools sit on the inside edge of the bubble, so they appear
              * between the message and the middle of the thread rather than
              * off the side of the screen. Hidden until the row is hovered,
              * and kept in the layout with `invisible` so nothing shifts as
              * the mouse crosses the thread.
              */}
            {message.direction === 'out' && !message.isPrivateNote && (
              <MessageTools
                message={message}
                open={reactingTo === message.id}
                onOpen={() => setReactingTo(reactingTo === message.id ? null : message.id)}
                onReply={() => setReplyingTo(message)}
                onReact={(emoji) => react.mutate({ id: message.id, emoji })}
                pending={react.isPending && react.variables?.id === message.id}
              />
            )}

            <div
              className={cx(
                'max-w-[75%] rounded-[var(--radius-lg)] px-3.5 py-2.5 text-sm',
                message.isPrivateNote
                  ? 'border border-dashed border-[var(--color-warn)]/50 bg-[var(--color-warn-soft)] text-[var(--color-ink)]'
                  : message.direction === 'out'
                    ? 'bg-crust-600 text-white'
                    : 'bg-[var(--color-surface-sunken)]',
              )}
            >
              {message.isPrivateNote && (
                <p className="mb-1 flex items-center gap-1 text-[11px] font-medium opacity-70">
                  <StickyNote className="size-3" /> Internal note
                </p>
              )}

              {/*
                * What this message was answering, quoted above it.
                *
                * Matched on the platform's id because that is what both ends
                * speak. A reply to something older than this thread's loaded
                * history finds nothing, and says so rather than drawing an
                * empty box — the quote is context, and missing context should
                * look missing.
                */}
              {message.replyToExternalId && (
                <div
                  className={cx(
                    'mb-1.5 border-l-2 pl-2 text-xs',
                    message.direction === 'out' && !message.isPrivateNote
                      ? 'border-white/40 text-white/70'
                      : 'border-[var(--color-line-strong)] text-[var(--color-ink-soft)]',
                  )}
                >
                  {(() => {
                    const quoted = data.messages.find((m) => m.externalId === message.replyToExternalId);
                    if (!quoted) return <span className="italic">an earlier message</span>;
                    return (
                      <>
                        <span className="block font-medium">
                          {quoted.direction === 'out' ? 'You' : data.contactName}
                        </span>
                        <span className="line-clamp-2 block">
                          {quoted.body || quoted.attachments[0]?.name || 'an attachment'}
                        </span>
                      </>
                    );
                  })()}
                </div>
              )}
              {/*
                * break-words as well as pre-wrap. Wrapping at whitespace alone
                * leaves a pasted tracking link — which has none — to run past
                * the bubble and drag a horizontal scrollbar across the whole
                * thread.
                */}
              {message.body && <p className="whitespace-pre-wrap break-words">{message.body}</p>}

              {message.attachments.length > 0 && (
                <div className={cx('space-y-1.5', message.body && 'mt-2')}>
                  {message.attachments.map((attachment, index) => (
                    <Attachment key={`${attachment.url}-${index}`} attachment={attachment} outbound={message.direction === 'out'} channel={data.channel} />
                  ))}
                </div>
              )}

              <p className={cx('mt-1 flex items-center gap-1 text-[11px]', message.direction === 'out' && !message.isPrivateNote ? 'text-white/60' : 'text-[var(--color-ink-faint)]')}>
                {relativeTime(message.sentAt)}
                {/*
                  * What became of it, for messages this shop sent.
                  *
                  * An internal note never leaves the building, so it has no
                  * delivery to report. Everything else says as much as the
                  * platform has told us and no more: a tick that claimed
                  * "delivered" on a channel that never reports would be a
                  * guess dressed as a fact.
                  */}
                {message.direction === 'out' && !message.isPrivateNote && <Receipt message={message} />}
              </p>

              {/*
                * Reactions sit under the bubble as Facebook puts them, and are
                * read-only: Meta reports what a customer reacts with and gives
                * no way to react back from here.
                */}
              {(message.reactions.length > 0 || message.ownReaction) && (
                <div
                  className="-mb-3 mt-1 flex w-fit items-center gap-0.5 rounded-full border border-[var(--color-line)] bg-[var(--color-surface)] px-1.5 py-0.5 elevation-raised"
                  title={[...message.reactions.map((r) => r.name ?? r.emoji), message.ownReaction && 'yours']
                    .filter(Boolean)
                    .join(', ')}
                >
                  {/*
                    * Ours is clickable — clicking it takes it off again. The
                    * customer's are not: theirs to place and theirs to remove.
                    */}
                  {message.ownReaction && (
                    <button
                      type="button"
                      title="Remove your reaction"
                      onClick={() => react.mutate({ id: message.id, emoji: null })}
                      className="text-sm leading-none transition hover:opacity-60"
                    >
                      {renderableEmoji(message.ownReaction)}
                    </button>
                  )}
                  {message.reactions.map((reaction, index) => (
                    <span key={`${reaction.actorId}-${index}`} className="text-sm leading-none">
                      {/*
                        * Also applied here, not only on the way in. Reactions
                        * stored before that fix landed are still bare code
                        * points in the database, and would go on drawing as
                        * an empty pill until someone reacted again.
                        */}
                      {renderableEmoji(reaction.emoji)}
                    </span>
                  ))}
                </div>
              )}
            </div>

            {message.direction === 'in' && (
              <MessageTools
                message={message}
                open={reactingTo === message.id}
                onOpen={() => setReactingTo(reactingTo === message.id ? null : message.id)}
                onReply={() => setReplyingTo(message)}
                onReact={(emoji) => react.mutate({ id: message.id, emoji })}
                pending={react.isPending && react.variables?.id === message.id}
              />
            )}
          </div>
        ))}
        <div ref={bottom} />
      </div>

      {/* Composer -------------------------------------------------------- */}
      <footer className="space-y-2 border-t border-[var(--color-line)] px-4 py-3 pb-safe">
        {/*
          * Said before the message is written, not after it is refused.
          *
          * Meta gives a Page 24 hours from the customer's last message. Past
          * that a plain reply comes back "(#10) ... outside of allowed
          * window", and the shop had three of them in one morning — each one
          * typed here, rejected, and then typed again in Messenger. The expiry
          * was on record the whole time; nothing showed it.
          *
          * An internal note never leaves the building, so it is exempt.
          */}
        {!isNote && window.state === 'closed' && (
          <div className="flex items-start gap-2 rounded-[var(--radius-md)] border border-[var(--color-warn)]/40 bg-[var(--color-warn-soft)] px-3 py-2 text-xs">
            <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-[var(--color-warn)]" />
            <p className="min-w-0 flex-1">
              {humanAgent === 'available' ? (
                <>
                  {/*
                    * The weekend case. The shop is shut from Friday evening to
                    * Monday morning, so every message that arrives in between
                    * is outside the 24 hours before anyone can read it — and
                    * answering on Monday is not spam, it is the reply.
                    */}
                  <span className="font-medium">Their 24-hour window closed {relativeTime(window.expiresAt)}.</span>{' '}
                  Your reply goes as a human-agent response, which {channelName} allows for 7 days after the
                  customer wrote. It needs the Page approved for Human Agent — if it comes back refused, that
                  approval is what is missing.
                </>
              ) : (
                <>
                  <span className="font-medium">{channelName} will not deliver a new message.</span> They last
                  wrote more than 7 days ago, which is past every allowance. It reopens the moment they write
                  again.
                </>
              )}
              {data.handoffUrl && (
                <>
                  {' '}
                  <a href={data.handoffUrl} target="_blank" rel="noreferrer" className="font-medium underline">
                    Open the chat in {channelName}
                  </a>
                  .
                </>
              )}
            </p>
          </div>
        )}

        {/* Time enough to notice, not so little that it is news. */}
        {!isNote && isClosingSoon(window) && (
          <p className="text-xs text-[var(--color-ink-faint)]">
            The reply window closes {relativeTime((window as { expiresAt: string }).expiresAt)}.
          </p>
        )}

        <div className="flex items-center gap-3 text-xs">
          <label className="flex items-center gap-1.5">
            <input type="checkbox" checked={isNote} onChange={(event) => setIsNote(event.target.checked)} className="size-3.5 accent-[var(--color-warn)]" />
            Internal note
          </label>

          {/*
            * The saved replies already existed on the server with no way to
            * reach them. A shop answering the same question forty times a day
            * should not be retyping the opening hours.
            */}
          <button
            type="button"
            onClick={() => setTemplatesOpen(true)}
            className="inline-flex items-center gap-1 text-crust-600 hover:underline"
          >
            <FileText className="size-3" /> Templates
          </button>

          {/*
            * The same thing the slash command does, for whoever reaches for a
            * button instead. Not offered on an internal note: a list of prices
            * is something you send a customer, not something you leave for the
            * team.
            */}
          {!isNote && (
            <button
              type="button"
              onClick={pasteAvailableNow}
              disabled={pastingMenu}
              title="Paste what the bake sheet says is available today"
              className="inline-flex items-center gap-1 text-crust-600 hover:underline disabled:opacity-50"
            >
              <ClipboardList className="size-3" /> Available now
            </button>
          )}

          {/*
            * Sending a picture back. An internal note is staff-only and never
            * leaves the building, so there is nothing to attach it to.
            */}
          {!isNote && (
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              disabled={sendFile.isPending}
              className="inline-flex items-center gap-1 text-crust-600 hover:underline disabled:opacity-50"
            >
              <Paperclip className="size-3" /> {sendFile.isPending ? 'Sending…' : 'Attach'}
            </button>
          )}
          <input
            ref={fileInput}
            type="file"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              // Cleared first: picking the same file twice in a row fires no
              // change event otherwise, and the second send silently does
              // nothing.
              event.target.value = '';
              if (file) sendFile.mutate(file);
            }}
          />

          {canPull && (
            <button
              type="button"
              onClick={() =>
                pullMissing.mutate(undefined, {
                  onSuccess: (result) =>
                    toast.push(
                      result.added > 0
                        ? { tone: 'ok', title: `${result.added} message${result.added === 1 ? '' : 's'} recovered` }
                        : { tone: 'neutral', title: 'Nothing missing — this thread is complete' },
                    ),
                  onError: () =>
                    toast.push({ tone: 'warn', title: 'Messenger would not hand over this thread just now' }),
                })
              }
              disabled={pullMissing.isPending}
              className="inline-flex items-center gap-1 text-crust-600 hover:underline disabled:opacity-50"
            >
              <RefreshCw className={cx('size-3', pullMissing.isPending && 'animate-spin')} />
              {pullMissing.isPending ? 'Checking…' : 'Check Messenger'}
            </button>
          )}

          {data.handoffUrl && (
            <a href={data.handoffUrl} target="_blank" rel="noreferrer" className="ml-auto inline-flex items-center gap-1 text-crust-600 hover:underline">
              <ExternalLink className="size-3" /> Open in {CHANNEL_LABEL[data.channel]}
            </a>
          )}
        </div>

        {replyingTo && !isNote && (
          <div className="mb-2 flex items-start gap-2 rounded-[var(--radius-md)] border-l-2 border-crust-500 bg-[var(--color-surface-sunken)] px-3 py-2 text-xs">
            <div className="min-w-0 flex-1">
              <span className="block font-medium">
                Replying to {replyingTo.direction === 'out' ? 'your message' : data.contactName}
              </span>
              <span className="line-clamp-2 block text-[var(--color-ink-soft)]">
                {replyingTo.body || replyingTo.attachments[0]?.name || 'an attachment'}
              </span>
            </div>
            <button
              type="button"
              aria-label="Stop replying to that message"
              onClick={() => setReplyingTo(null)}
              className="shrink-0 rounded p-1 text-[var(--color-ink-faint)] hover:bg-[var(--color-surface)]"
            >
              <X className="size-3.5" />
            </button>
          </div>
        )}

        <div className="relative flex gap-2">
          {suggestions.length > 0 && (
            <div className="absolute bottom-full left-0 right-0 z-20 mb-2 overflow-hidden rounded-[var(--radius-md)] border border-[var(--color-line-strong)] bg-[var(--color-surface)] shadow-lg">
              {suggestions.map((option, index) => (
                <button
                  key={option.id}
                  type="button"
                  disabled={!option.ready}
                  onMouseEnter={() => setSuggestion(index)}
                  onClick={() => useSuggestion(option)}
                  className={cx(
                    'block w-full border-b border-[var(--color-line)] px-3 py-2 text-left last:border-b-0 disabled:opacity-70',
                    index === suggestion ? 'bg-[var(--color-surface-sunken)]' : 'hover:bg-[var(--color-surface-sunken)]',
                  )}
                >
                  <span className="block text-sm font-medium">{option.title}</span>
                  <span className="block truncate text-xs text-[var(--color-ink-soft)]">{option.preview}</span>
                </button>
              ))}
              <p className="bg-[var(--color-surface-sunken)] px-3 py-1.5 text-[11px] text-[var(--color-ink-faint)]">
                ↑↓ to choose · Tab for the first · Esc to hide
              </p>
            </div>
          )}
          <Textarea
            rows={4}
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value);
              // Typing again undoes an Esc, so the list is never off for good.
              setSuggestionsOff(false);
              setSuggestion(-1);
            }}
            onKeyDown={(event) => {
              if (suggestions.length > 0) {
                if (event.key === 'ArrowDown') {
                  event.preventDefault();
                  setSuggestion((c) => (c + 1) % suggestions.length);
                  return;
                }
                if (event.key === 'ArrowUp') {
                  event.preventDefault();
                  setSuggestion((c) => (c <= 0 ? suggestions.length - 1 : c - 1));
                  return;
                }
                if (event.key === 'Escape') {
                  event.preventDefault();
                  setSuggestionsOff(true);
                  return;
                }
                if (event.key === 'Tab') {
                  event.preventDefault();
                  useSuggestion(suggestions[Math.max(suggestion, 0)]!);
                  return;
                }
                // Only once a row is actually highlighted. An open list must not
                // quietly turn the send key into an insert key.
                if (event.key === 'Enter' && !event.shiftKey && suggestion >= 0) {
                  event.preventDefault();
                  useSuggestion(suggestions[suggestion]!);
                  return;
                }
              }
              /*
               * At a desk, Enter sends and Shift+Enter makes a line. On a
               * phone it only makes a line and the Send button sends, because
               * a touch keyboard has no Shift to pair with its return key —
               * see `enterConfirms`.
               */
              if (enterConfirms() && event.key === 'Enter' && !event.shiftKey && draft.trim()) {
                event.preventDefault();
                reply.mutate();
              }
            }}
            onPaste={(event) => {
              /*
               * A screenshot usually arrives on the clipboard, not as a file on
               * disk, and having to save it first to attach it is the kind of
               * detour that sends people back to Messenger.
               */
              if (isNote) return;
              const file = [...event.clipboardData.files][0];
              if (!file) return;
              event.preventDefault();
              sendFile.mutate(file);
            }}
            placeholder={isNote ? 'Note for the team, not sent to the customer…' : 'Write a reply…'}
            /*
             * Four lines to start, and draggable from the bottom-right corner.
             *
             * Two lines meant anything longer than a sentence was typed through
             * a letterbox — a menu, directions to a delivery address, an
             * explanation of why an order is late — with the top of it scrolled
             * out of sight while you wrote the end.
             *
             * Vertical only: this sits in a flex row beside the Send button, and
             * a horizontal drag would fight the layout rather than widen
             * anything. The ceiling keeps a tall drag from swallowing the
             * conversation above it.
             */
            className="max-h-[50vh] min-h-[4rem] flex-1 resize-y"
          />
          <Button
            variant="primary"
            className="self-end"
            icon={<Send className="size-4" />}
            loading={reply.isPending}
            disabled={!draft.trim()}
            onClick={() => reply.mutate()}
          >
            Send
          </Button>
        </div>
      </footer>

      {templatesOpen && (
        <TemplatePicker
          onClose={() => setTemplatesOpen(false)}
          onPick={(body) => {
            // Appended, not replaced: half a reply may already be typed.
            setDraft((current) => (current.trim() ? `${current.trimEnd()}\n\n${body}` : body));
            setTemplatesOpen(false);
          }}
        />
      )}

      {openCustomer && <CustomerDetail customerId={openCustomer} onClose={() => setOpenCustomer(null)} />}
    </div>
  );
}

/** Saved replies, for the questions a bakery answers forty times a day. */
function TemplatePicker({ onClose, onPick }: { onClose: () => void; onPick: (body: string) => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(false);
  /** The template being changed, or null when the form is writing a new one. */
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState({ title: '', body: '' });
  const composing = adding || editingId !== null;

  const replies = useQuery({ queryKey: ['canned-replies'], queryFn: api.inbox.cannedReplies });

  const closeForm = () => {
    setAdding(false);
    setEditingId(null);
    setDraft({ title: '', body: '' });
  };

  const update = useMutation({
    mutationFn: () => api.inbox.updateCannedReply(editingId!, { title: draft.title, body: draft.body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['canned-replies'] });
      closeForm();
      toast.push({ tone: 'ok', title: 'Template updated' });
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not save', body: error.message }),
  });

  const create = useMutation({
    mutationFn: () => api.inbox.createCannedReply({ title: draft.title, body: draft.body }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['canned-replies'] });
      setDraft({ title: '', body: '' });
      setAdding(false);
      toast.push({ tone: 'ok', title: 'Template saved' });
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not save', body: error.message }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.inbox.deleteCannedReply(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['canned-replies'] }),
  });

  const term = search.trim().toLowerCase();
  const matches = (replies.data ?? []).filter(
    (r) => !term || r.title.toLowerCase().includes(term) || r.body.toLowerCase().includes(term),
  );

  return (
    <Modal
      open
      onClose={onClose}
      title="Templates"
      description="Saved replies. Picking one drops it into the box so it can still be edited before sending."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          <Button variant="secondary" onClick={() => (composing ? closeForm() : setAdding(true))}>
            {composing ? 'Cancel' : 'New template'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {composing ? (
          <div className="space-y-3">
            <Field label="Title" required>
              <Input
                value={draft.title}
                onChange={(event) => setDraft((c) => ({ ...c, title: event.target.value }))}
                placeholder="Opening hours"
                autoFocus
              />
            </Field>
            <Field
              label="Message"
              required
              hint="Line breaks are kept exactly as you type them."
            >
              <Textarea
                rows={8}
                value={draft.body}
                onChange={(event) => setDraft((c) => ({ ...c, body: event.target.value }))}
                placeholder="We are open Monday to Friday, 9 AM to 6 PM."
                className="resize-y"
              />
            </Field>
            <Button
              variant="primary"
              loading={create.isPending || update.isPending}
              disabled={!draft.title.trim() || !draft.body.trim()}
              onClick={() => (editingId ? update.mutate() : create.mutate())}
            >
              {editingId ? 'Save changes' : 'Save template'}
            </Button>
          </div>
        ) : (
          <>
            <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search templates…" autoFocus />

            {replies.isLoading ? (
              <Skeleton className="h-32" />
            ) : matches.length === 0 ? (
              <p className="py-6 text-center text-sm text-[var(--color-ink-faint)]">
                {replies.data?.length ? 'Nothing matches that.' : 'No templates saved yet.'}
              </p>
            ) : (
              <ul className="max-h-80 space-y-1.5 overflow-y-auto">
                {matches.map((template) => (
                  <li key={template.id} className="flex items-start gap-2">
                    <button
                      type="button"
                      onClick={() => onPick(template.body)}
                      className="min-w-0 flex-1 rounded-[var(--radius-md)] border border-[var(--color-line)] px-3 py-2 text-left hover:bg-[var(--color-surface-sunken)]"
                    >
                      <span className="block text-sm font-medium">{template.title}</span>
                      <span className="mt-0.5 line-clamp-2 block text-xs text-[var(--color-ink-soft)]">
                        {template.body}
                      </span>
                    </button>
                    <Button
                      variant="ghost"
                      aria-label={`Edit ${template.title}`}
                      className="shrink-0 px-2"
                      onClick={() => {
                        setDraft({ title: template.title, body: template.body });
                        setEditingId(template.id);
                      }}
                      icon={<Pencil className="size-4" />}
                    />
                    <Button
                      variant="ghost"
                      aria-label={`Delete ${template.title}`}
                      className="shrink-0 px-2 text-[var(--color-danger)] hover:bg-[var(--color-danger)]/10"
                      onClick={() => remove.mutate(template.id)}
                      icon={<Trash2 className="size-4" />}
                    />
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}

/** Build an order without leaving the conversation. */
/**
 * Writing an order down while the customer is still talking.
 *
 * A panel and not a dialog, because the thing being copied is on the screen
 * behind it. Six pastries with a note on two of them cannot be held in the
 * head across a dimmed backdrop, and the shop was closing the form to re-read
 * the message and losing the lines already entered.
 *
 * It sits beside the conversation from large screens up, where the thread
 * stays lit, scrollable and answerable — a clarifying question can go out and
 * be read without giving up the half-finished order. On a phone there is
 * nothing to sit beside, so it covers the screen as the dialog used to.
 */
function OrderPanel({ conversationId, onClose }: { conversationId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const products = useQuery({ queryKey: ['products'], queryFn: () => api.catalog.products() });

  /*
   * A–Z rather than the catalogue's own order, which exists to arrange the
   * till's grid and only makes a long dropdown harder to read. See
   * lib/catalog-order.
   */
  const pickable = useMemo(() => byName(products.data ?? []), [products.data]);

  const discounts = useQuery({ queryKey: ['discounts'], queryFn: () => api.catalog.discounts() });

  const [lines, setLines] = useState<{ productId: string; qty: number; discountAmount: number; notes?: string | null }[]>([]);
  /** Which draft line the note box is open for. */
  const [notingLine, setNotingLine] = useState<number | null>(null);
  const [fulfillment, setFulfillment] = useState('pickup');
  const [dueDate, setDueDate] = useState(todayLocal);
  const [dueTime, setDueTime] = useState(DEFAULT_DUE_TIME.pickup);
  const [discountId, setDiscountId] = useState('');
  /** Which line the per-item discount sheet is open for. */
  const [discounting, setDiscounting] = useState<number | null>(null);

  /*
   * Escape closes, as it did when this was a dialog — but only when it is the
   * outermost thing open. The note and discount sheets are dialogs in their
   * own right, and their Escape must dismiss them alone rather than throwing
   * away the order underneath on the way past.
   */
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && notingLine === null && discounting === null) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, notingLine, discounting]);

  /*
   * Switching between pickup and delivery moves the time to that kind's usual
   * hour — but only until somebody picks one themselves. Overwriting a chosen
   * time because the fulfilment changed would quietly lose a real decision.
   */
  const [timeChosen, setTimeChosen] = useState(false);

  const changeFulfillment = (value: string) => {
    setFulfillment(value);
    if (!timeChosen) setDueTime(DEFAULT_DUE_TIME[value] ?? DEFAULT_DUE_TIME.pickup);
  };

  /*
   * Same split as the till: a whole-order discount comes off the subtotal, a
   * per-item one goes on a single line. Offering either in the other's place is
   * how a statutory 20% ends up on one pastry.
   */
  const orderDiscounts = (discounts.data ?? []).filter((d) => d.scope === 'order' || d.scope === 'both');
  const itemDiscounts = (discounts.data ?? []).filter((d) => d.scope === 'item' || d.scope === 'both');
  const selectedDiscount = orderDiscounts.find((d) => d.id === discountId) ?? null;

  /*
   * Totalled with the same function the server commits with, rather than a
   * sum of price × quantity written out here. That hand-rolled version knew
   * nothing about discounts, so the figure on the button would have parted
   * company with the order the moment one was given.
   */
  const totals = computeTotals({
    lines: lines.map((line) => {
      const product = products.data?.find((p) => p.id === line.productId);
      return {
        productId: line.productId,
        name: product?.name ?? 'Item',
        qty: line.qty,
        unitPrice: product?.price ?? 0,
        discountAmount: line.discountAmount,
        costPerUnit: product?.computedCost ?? 0,
        notes: line.notes ?? null,
      };
    }),
    discount: selectedDiscount,
  });
  const total = totals.total;

  const create = useMutation({
    mutationFn: () =>
      api.inbox.createOrder(conversationId, {
        lines,
        discountId: discountId || null,
        fulfillment,
        // `2026-09-22T16:00` with no zone is read as local time, which is the
        // shop's clock — exactly what was picked.
        dueAt: dueDate && dueTime ? new Date(`${dueDate}T${dueTime}`).toISOString() : null,
      }),
    onSuccess: ({ order, notification }) => {
      queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
      ordersChanged(queryClient);
      toast.push({
        tone: 'ok',
        title: `${order.orderNo} created`,
        body:
          notification?.status === 'sent'
            ? 'Order summary sent to the customer.'
            : (notification?.error ?? undefined),
        action: notification?.handoffUrl ? { label: 'Send the summary', href: notification.handoffUrl } : undefined,
      });
      onClose();
    },
    onError: (error: Error) => toast.push({ tone: 'danger', title: 'Could not create', body: error.message }),
  });

  return (
    <aside
      aria-label="Order from this conversation"
      className={cx(
        // On a phone there is nothing to sit beside, so it takes the screen.
        'fixed inset-0 z-50 flex flex-col bg-[var(--color-surface)]',
        // From large up it is a column of its own, and the conversation next
        // to it stays lit. That is the whole reason this is not a dialog.
        'lg:static lg:z-auto lg:w-[23rem] lg:shrink-0 lg:border-l lg:border-[var(--color-line)] xl:w-[26rem]',
      )}
    >
      <header className="flex items-start gap-2 border-b border-[var(--color-line)] px-4 py-3">
        <div className="min-w-0 flex-1">
          <h2 className="font-display text-base font-semibold">Order from this conversation</h2>
          <p className="text-xs text-[var(--color-ink-soft)]">
            The customer, the channel and the thread are all linked automatically.
          </p>
        </div>
        <Button
          variant="ghost"
          aria-label="Close without creating the order"
          title="Close"
          className="shrink-0 px-2"
          onClick={onClose}
          icon={<X className="size-4" />}
        />
      </header>

      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-4">
        {/*
          * Not a fixed split.
          *
          * A date and a time side by side need about 250px between them, and
          * three columns of a five-column grid in a panel this width is 225 —
          * so whichever share the grid handed out, something had to give, and
          * what gave was the year on the date. Sized to what each pair actually
          * measures instead, and allowed to wrap onto a second line when the
          * panel is too narrow to hold both. A wrapped row is readable; a
          * clipped date is not.
          */}
        <div className="flex flex-wrap gap-3">
          <Field label="Fulfilment" className="min-w-[6.5rem] flex-1">
            <Select value={fulfillment} onChange={(event) => changeFulfillment(event.target.value)}>
              <option value="pickup">Pickup</option>
              <option value="delivery">Delivery</option>
            </Select>
          </Field>
          <Field label="Needed by" className="min-w-[16.5rem] flex-[2]">
            <div className="flex gap-2">
              {/*
                * Both get a floor, and neither is pinned to a fixed width.
                *
                * The date used to take `flex-1` against a fixed-width time, so
                * every pixel the panel was short came off the date alone — down
                * to 26px, showing "10/04" with the year gone. Pinning the time
                * instead then clipped "4:00 PM" to "4:00 PN".
                *
                * The floors are measured from the longest thing each has to
                * show — "10/04/2026" with its calendar button, "12:00 AM" with
                * the dropdown arrow — plus room to spare. The slack is the
                * point: text is a few pixels wider on some machines than
                * others, and a control sized to the exact measurement on one
                * screen is a control that clips on the next.
                */}
              <Input
                type="date"
                value={dueDate}
                onChange={(event) => setDueDate(event.target.value)}
                className="min-w-[8rem] flex-1"
              />
              <Select
                value={dueTime}
                onChange={(event) => {
                  setDueTime(event.target.value);
                  setTimeChosen(true);
                }}
                className="min-w-[7.75rem] flex-1"
              >
                {TIME_SLOTS.map((slot) => (
                  <option key={slot} value={slot}>
                    {slotLabel(slot)}
                  </option>
                ))}
              </Select>
            </div>
          </Field>
        </div>

        <div className="space-y-2">
          {/*
            * The product gets the whole width and the controls sit under it.
            *
            * All of it used to share one row, so in a panel this narrow the
            * name was the only thing that could give — "Cheese 'n Herbs Spread
            * (120g)" showed as "Cheese 'n Herbs S" and the one thing somebody
            * needs to check before pressing Create was the one thing they
            * could not read.
            */}
          {lines.map((line, index) => {
            const chosen = products.data?.find((product) => product.id === line.productId);
            const named = chosen?.name ?? 'this item';
            return (
              <div
                key={index}
                className="space-y-1.5 rounded-[var(--radius-md)] border border-[var(--color-line)] p-2"
              >
                <Select
                  value={line.productId}
                  onChange={(event) =>
                    setLines((c) => c.map((l, i) => (i === index ? { ...l, productId: event.target.value } : l)))
                  }
                  className="w-full"
                >
                  {pickable.map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name} — {formatMoney(product.price)}
                    </option>
                  ))}
                </Select>

                <div className="flex items-center gap-2">
                  <NumberInput
                    inputMode="numeric"
                    value={line.qty}
                    onChange={(qty) => setLines((c) => c.map((l, i) => (i === index ? { ...l, qty } : l)))}
                    aria-label={`How many ${named}`}
                    className="tabular w-16 shrink-0 text-center"
                  />

                  {/*
                    * A discount on this line alone — day-old bread going out
                    * with fresh, on the same order. The same control as the till.
                    */}
                  {itemDiscounts.length > 0 && (
                    <Button
                      variant="ghost"
                      aria-label={`Discount ${named}`}
                      title="Discount this item"
                      className={cx('shrink-0 px-2.5', line.discountAmount > 0 && 'text-[var(--color-ok)]')}
                      onClick={() => setDiscounting(index)}
                      icon={<Percent className="size-4" />}
                    />
                  )}
                  <Button
                    variant="ghost"
                    aria-label={`Note on ${named}`}
                    title="Add a note to this item"
                    className={cx('shrink-0 px-2.5', line.notes && 'text-crust-600')}
                    onClick={() => setNotingLine(index)}
                    icon={<MessageSquare className="size-4" />}
                  />
                  <Button
                    variant="ghost"
                    aria-label={`Remove ${named}`}
                    title="Remove this item"
                    className="shrink-0 px-2.5 text-[var(--color-danger)] hover:bg-[var(--color-danger)]/10"
                    onClick={() => setLines((current) => current.filter((_, i) => i !== index))}
                    icon={<Trash2 className="size-4" />}
                  />

                  {/*
                    * What this line comes to, in the space the controls leave.
                    * Three of one good at ₱200 is the sort of thing worth
                    * catching before the order is created, not after.
                    */}
                  <span className="tabular ml-auto shrink-0 text-sm font-medium">
                    {formatMoney(totals.lines[index]?.lineTotal ?? 0)}
                  </span>
                </div>

                {/* The note itself, once there is one — otherwise it is a flag
                    on a button nobody opens again to re-read. */}
                {line.notes && (
                  <p className="truncate text-xs italic text-[var(--color-ink-soft)]" title={line.notes}>
                    {line.notes}
                  </p>
                )}
              </div>
            );
          })}

          {/* What each line comes to once its own discount is off. */}
          {lines.some((line) => line.discountAmount > 0) && (
            <ul className="space-y-0.5 pl-1 text-xs text-[var(--color-ink-faint)]">
              {totals.lines.map((line, index) =>
                line.discountAmount > 0 ? (
                  <li key={index} className="tabular flex justify-between">
                    <span className="truncate">{line.name}</span>
                    <span className="text-[var(--color-ok)]">
                      −{formatMoney(line.discountAmount)} → {formatMoney(line.lineTotal)}
                    </span>
                  </li>
                ) : null,
              )}
            </ul>
          )}
          <Button
            size="sm"
            icon={<ShoppingBag className="size-4" />}
            disabled={!products.data?.length}
            onClick={() =>
              setLines((current) => [...current, { productId: products.data![0]!.id, qty: 1, discountAmount: 0, notes: null }])
            }
          >
            Add an item
          </Button>
        </div>

        {/* The whole-order discount, named so it is not mistaken for the
            per-line one on the button beside each item. */}
        {orderDiscounts.length > 0 && (
          <Field label="Discount on the whole order">
            <Select value={discountId} onChange={(event) => setDiscountId(event.target.value)}>
              <option value="">No discount on the whole order</option>
              {orderDiscounts.map((discount) => (
                <option key={discount.id} value={discount.id}>{discount.name}</option>
              ))}
            </Select>
          </Field>
        )}

        {(totals.discountAmount > 0 || totals.subtotal !== total) && (
          <dl className="space-y-1 border-t border-[var(--color-line)] pt-3 text-sm">
            <div className="flex justify-between">
              <dt className="text-[var(--color-ink-soft)]">Subtotal</dt>
              <dd className="tabular">{formatMoney(totals.subtotal)}</dd>
            </div>
            {totals.discountAmount > 0 && (
              <div className="flex justify-between text-[var(--color-ok)]">
                <dt>Discount on the order</dt>
                <dd className="tabular">−{formatMoney(totals.discountAmount)}</dd>
              </div>
            )}
          </dl>
        )}
      </div>

      <footer className="flex items-center justify-end gap-2 border-t border-[var(--color-line)] px-4 py-3">
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button variant="primary" loading={create.isPending} disabled={lines.length === 0} onClick={() => create.mutate()}>
          Create {formatMoney(total)}
        </Button>
      </footer>

      {notingLine !== null && lines[notingLine] && (
        <ItemNoteModal
          line={{
            name: products.data?.find((p) => p.id === lines[notingLine]!.productId)?.name ?? 'Item',
            notes: lines[notingLine]!.notes,
          }}
          onClose={() => setNotingLine(null)}
          onSave={(notes) => {
            setLines((current) =>
              current.map((line, i) => (i === notingLine ? { ...line, notes: notes.trim() || null } : line)),
            );
            setNotingLine(null);
          }}
        />
      )}

      {discounting !== null && totals.lines[discounting] && (
        <LineDiscountModal
          line={totals.lines[discounting]!}
          discounts={itemDiscounts}
          onClose={() => setDiscounting(null)}
          onApply={(centavos) => {
            setLines((current) =>
              current.map((line, i) => (i === discounting ? { ...line, discountAmount: centavos } : line)),
            );
            setDiscounting(null);
          }}
        />
      )}
    </aside>
  );
}

/**
 * What the shop actually reacts with.
 *
 * Not Facebook's own six. Meta's API takes any emoji, and the useful set for a
 * bakery is not the set for a social feed: ✅ and ❌ answer "can you do this?"
 * in one tap, and 👌 acknowledges an instruction without promising anything.
 * 🙇 carries the apology and the thanks that 'pasensya na po' and 'salamat po'
 * do, which no face in the set manages on its own.
 * Anger is not a reaction to send a customer, so it is not offered.
 *
 * Deliberately short. This is a row of buttons over a chat, not a keyboard.
 */
const REACTIONS = ['❤️', '🥰', '😄', '😮', '😢', '👍', '👌', '🙇', '✅', '❌'] as const;

/**
 * Reply and react.
 *
 * Kept in the layout at all times and merely made invisible, so the thread
 * does not jump as the pointer crosses it. Focus reveals them too, which is
 * what makes them reachable without a mouse.
 *
 * On a touch screen they are simply always visible. Revealing them on hover
 * is right for a mouse and meaningless for a thumb — there is no hover to
 * give, so on a phone these buttons had never appeared at all and replying to
 * a particular message or reacting to one was impossible there. The space is
 * reserved either way, so showing them costs no layout.
 */
function MessageTools({
  message,
  open,
  onOpen,
  onReply,
  onReact,
  pending,
}: {
  message: Message;
  open: boolean;
  onOpen: () => void;
  onReply: () => void;
  onReact: (emoji: string | null) => void;
  pending: boolean;
}) {
  /*
   * Nothing to react to until the platform has given the message an id, and
   * an internal note has no platform at all. Replying is refused for the same
   * reason: there is no id to quote.
   */
  const reachable = Boolean(message.externalId) && !message.isPrivateNote;
  if (!reachable) return <div className="w-[52px] shrink-0" aria-hidden />;

  /*
   * Which way the row of emoji opens.
   *
   * The tools sit on the inside edge of their bubble, so a long message pushes
   * them right up against the side of the thread — and a row centred on them
   * then hangs off it, over the conversation list on one side or off the pane
   * on the other. Opening it towards the middle instead keeps it on screen
   * whatever the length of the message it belongs to.
   */
  const outbound = message.direction === 'out';

  return (
    <div className="flex shrink-0 items-center gap-0.5">
      <button
        type="button"
        title="Reply to this message"
        aria-label="Reply to this message"
        onClick={onReply}
        className={cx(
          'invisible rounded-full p-1.5 text-[var(--color-ink-faint)] transition',
          'hover:bg-[var(--color-surface-sunken)] hover:text-[var(--color-ink)]',
          'focus-visible:visible group-hover:visible',
          // No hover on a thumb, so these stay on.
          '[@media(pointer:coarse)]:visible',
        )}
      >
        <Reply className="size-4" />
      </button>
      <button
        type="button"
        title="React to this message"
        aria-label="React to this message"
        onClick={onOpen}
        disabled={pending}
        className={cx(
          'rounded-full p-1.5 text-[var(--color-ink-faint)] transition hover:bg-[var(--color-surface-sunken)] hover:text-[var(--color-ink)] focus-visible:visible group-hover:visible',
          '[@media(pointer:coarse)]:visible',
          open || pending ? 'visible' : 'invisible',
        )}
      >
        <SmilePlus className="size-4" />
      </button>

      {open && (
        <div
          className={cx(
            'absolute bottom-full z-20 mb-1 flex gap-0.5 rounded-full border border-[var(--color-line)]',
            'bg-[var(--color-surface)] px-1.5 py-1 elevation-raised',
            // Over the bubble it belongs to, and never past the side of the thread.
            outbound ? 'right-0' : 'left-0',
          )}
        >
          {REACTIONS.map((emoji) => (
            <button
              key={emoji}
              type="button"
              aria-label={`React with ${emoji}`}
              onClick={() => onReact(emoji)}
              className={cx(
                'rounded-full px-1 py-0.5 text-base leading-none transition hover:scale-125',
                message.ownReaction === emoji && 'bg-[var(--color-surface-sunken)]',
              )}
            >
              {emoji}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
